import styles from './environmentModal.module.scss';
import { useTranslation } from "react-i18next";
import { Input } from "../../shared/components/input/input";
import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import {
  FieldValues,
  useFieldArray,
  useForm,
  useWatch
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  array,
  number,
  object,
  string
} from "yup";
import {
  Environment,
  EnvironmentHeader,
  EnvironmentVariable
} from "../../types/environmentTypes";
import { MessageWindow } from "../../shared/components/messageWindow/messageWindow";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../store";
import {
  Dropdown,
  DropdownItem
} from "../../shared/components/dropdown/dropdown";
import { useGetAllTestRunsQuery } from "../../api/testrailService";
import addBlockIcon from "../../shared/images/addBlock.svg";
import removeBlock from "../../shared/images/removeBlock.svg";
import {
  useCreateEnvironmentMutation,
  useDeleteEnvironmentMutation,
  useUpdateEnvironmentMutation
} from "../../api/environmentService";
import { ModalWindow } from "../../shared/components/modalWindow/modalWindow";
import { useGetAllVariablesQuery } from "../../api/variableService";
import { Accordion } from "../../shared/components/accordion/accordion";
import arrowIcon from "../../shared/images/arrow.svg";
import { TextArea } from "../../shared/components/textArea/textArea";
import { ParametersInput } from "../../shared/components/parametersInput/parametersInput";
import { getNonUniqueIndices } from "../../utils/shared.utils";
import { setUpdateModal } from "../../store/slices/mainSlice";
import {
  transformFieldForBackend,
  transformFieldForUser
} from "../../utils/testCaseUtils";

type EnvironmentModalProps = {
  onClose: (isSave: boolean) => void;
  environment?: Environment;
  showDelete?: boolean;
}

export const EnvironmentModal = ({ onClose, environment, showDelete }: EnvironmentModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const isSandbox = useSelector((state: RootState) => state.authSlice.isSandbox);
  const selectedVariables = useSelector((state: RootState) => state.mainSlice.selectedVariables);

  const [showDeleteMessage, setShowDeleteMessage] = useState<boolean>(false);

  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const [defaultHeaderActiveIndex, setDefaultHeaderActiveIndex] = useState<number | undefined>(0);

  const { data: testRuns } = useGetAllTestRunsQuery("", { skip: isSandbox });
  const { data: variables } = useGetAllVariablesQuery("");

  const [createEnvironment, { data: createData, isSuccess: isSuccessCreate, isLoading: isLoadingCreate }] = useCreateEnvironmentMutation();
  const [updateEnvironment, { isSuccess: isSuccessUpdate, isLoading: isLoadingUpdate }] = useUpdateEnvironmentMutation();
  const [deleteEnvironment, { isSuccess: isSuccessDelete, isLoading: isLoadingDelete }] = useDeleteEnvironmentMutation();

  useEffect(() => {
    if ((isSuccessCreate && createData) || isSuccessUpdate || isSuccessDelete) {
      onClose(true);
    }
  }, [onClose, isSuccessCreate, createData, isSuccessUpdate, isSuccessDelete]);

  useEffect(() => {
    if ((isSuccessCreate && createData) || isSuccessUpdate) {
      dispatch(setUpdateModal({ show: true, isDelete: false }));
    }
  }, [isSuccessCreate, createData, isSuccessUpdate]);

  useEffect(() => {
    if (isSuccessDelete) {
      dispatch(setUpdateModal({ show: true, isDelete: true }));
    }
  }, [isSuccessDelete]);

  const schema = object({
    name: string().required(t("common.notValid")).typeError(t("common.notValid")),
    testRun: isSandbox ? number() : number().required(t("common.notValid")),
    servers: array().required().of(
      object().shape({
        externalId: number(),
        name: string().required(t("common.notValid")),
        url: string().required(t("common.notValid")),
        authToken: string()
      })
    ),
    defaultHeaders: array().required().of(
      object().shape({
        externalId: number(),
        name: string().required(t("common.notValid")),
        value: string().required(t("common.notValid"))
      })
    ).test("unique", (value) => value.length === new Set(value.map(item => item.name))?.size),
    defaultVariableValues: array().required().of(
      object().shape({
        variableId: number().required(t("common.notValid")),
        variableValueId: number().required(t("common.notValid"))
      })
    )
  });

  const defaultValues = useMemo(() => {
    const defaultVariableValues: Array<EnvironmentVariable> = [];

    if (variables) {
      variables.forEach(variable => {
        const environmentVariable = environment?.defaultVariableValues?.find(item => item.variableId === variable.id);
        defaultVariableValues.push({
          variableId: variable.id,
          variableValueId: environmentVariable?.variableValueId || variable.values[0].id
        });
      });
    }

    let testRunId = environment?.testRunId;
    if (!!testRuns && !environment?.testRunId) {
      testRunId = testRuns[0].id;
    }

    return {
      name: environment?.name || "",
      servers: environment?.servers.map(server => (
        {
          externalId: server.id,
          authToken: server.authToken || "",
          name: server.name,
          url: server.url,
        })) || [{ name: "", url: "", authToken: "", externalId: 0 }],
      testRun: testRunId,
      defaultVariableValues,
      defaultHeaders: environment?.defaultHeaders.map(item => ({
          externalId: item.id,
          name: item.name,
          value: transformFieldForUser(selectedVariables, item.value)
      })) || []
    }
  }, [environment, variables, selectedVariables, testRuns]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
    mode: "onChange"
  });

  const {
    fields: serversFields,
    append: appendServer,
    remove: removeServer
  } = useFieldArray({control, name: "servers"});

  const {
    fields: defaultVariablesFields,
  } = useFieldArray({control, name: "defaultVariableValues"});

  const {
    fields: defaultHeadersFields,
    append: appendDefaultHeaders,
    remove: removeDefaultHeaders
  } = useFieldArray({control, name: "defaultHeaders"});
  const defaultHeadersWatch = useWatch({control, name: "defaultHeaders"});

  const addDefaultHeader = useCallback((e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    appendDefaultHeaders([{name: "", value: ""}]);
    setDefaultHeaderActiveIndex(0);
  }, [appendDefaultHeaders, setDefaultHeaderActiveIndex]);

  const getDefaultHeadersErrorName = (index: number): string | undefined => {
    if (getNonUniqueIndices(defaultHeadersWatch, "name").includes(index)) {
      return t("common.notUnique");
    } else {
      return !!errors.defaultHeaders?.[index]?.name ? errors.defaultHeaders?.[index]?.name?.message : "";
    }
  }

  useEffect(() => {
    if (variables && (isSandbox || !!testRuns)) {
      reset(defaultValues);
    }
  }, [variables, isSandbox, testRuns]);

  const onCancel = useCallback(() => {
    if (isDirty) {
      setShowConfirmCancel(true);
    } else {
      onClose(false);
    }
  }, [isDirty, onClose]);

  const closeConfirmCancelModal = useCallback((isConfirm: boolean) => {
    if (isConfirm) {
      onClose(false);
    }
    setShowConfirmCancel(false);
  }, [setShowConfirmCancel]);

  const transformHeadersForBackend = useCallback((headers: Array<EnvironmentHeader>): Array<EnvironmentHeader> => {
    return headers.map(header => ({
      ...header,
      value: transformFieldForBackend(selectedVariables, header.value)
    }));
  }, [selectedVariables]);

  const getDefaultHeaders = useCallback((value: { defaultHeaders: FieldValues[] }): Array<EnvironmentHeader> => {
    const defaultValue: Array<EnvironmentHeader> = value.defaultHeaders.map(item => ({
      id: item.externalId,
      name: item.name,
      value: item.value || "",
    }));
    if (!!environment?.id) {
      const result: Array<EnvironmentHeader> = [];
      environment.defaultHeaders.forEach(header => {
        const find = defaultValue.find(item => item.id === header.id);
        if (!find) {
          result.push({...header, isDeleted: true});
        }
      });
      result.push(...defaultValue);
      return transformHeadersForBackend(result);
    } else {
      return transformHeadersForBackend(defaultValue);
    }
  }, [environment]);

  const onClickSave = (): void => {
    handleSubmit((result) => {
      const newEnvironment: Environment = {
        id: environment?.id || 0,
        name: result.name,
        testRunId: result.testRun,
        servers: result.servers.map(server => ({
          id: server.externalId,
          name: server.name,
          url: server.url,
          authToken: server.authToken,
        })),
        defaultVariableValues: result.defaultVariableValues,
        defaultHeaders: getDefaultHeaders(result),
      };
      if (!!environment) {
        initUpdate(newEnvironment);
      } else {
        createEnvironment(newEnvironment);
      }
    }, (errors) => {
      if (errors.defaultHeaders) {
        setDefaultHeaderActiveIndex(0);
      }
    })();
  }

  const initUpdate = (newEnvironment: Environment): void => {
    if (environment) {
      environment.servers.forEach(server => {
        const findServer = newEnvironment.servers.find(item => item.id === server.id);
        if (!!findServer) {
          if (findServer.name !== server.name || findServer.url !== server.url || findServer.authToken !== server.authToken) {
            findServer.isUpdated = true;
          }
        } else {
          newEnvironment.servers.push({...server, isDeleted: true});
        }
      });
      updateEnvironment(newEnvironment);
    }
  }

  const onDeleteClick = (): void => {
    setShowDeleteMessage(true);
  }

  const closeMessageWindow = (isConfirm: boolean): void => {
    if (isConfirm && !!environment?.id) {
      deleteEnvironment(environment.id);
    }
    setShowDeleteMessage(false);
  }

  const removeServerClick = (index: number): void => {
    if (serversFields.length !== 1) {
      removeServer(index);
    }
  }

  const testRunDropdownItems: Array<DropdownItem> = useMemo(() => {
    return testRuns?.map(item => ({
      id: item.id,
      name: item.name,
      title: item.name
    })) || [];
  }, [testRuns]);

  const getVariable = useCallback((variableId: number) => {
    return variables?.find(item => item.id === variableId);
  }, [variables]);

  const getVariableName = useCallback((variableId: number) => {
    return getVariable(variableId)?.name || "";
  }, [getVariable]);

  const getVariableDropdownItems = useCallback((variableId: number): Array<DropdownItem> => {
    return getVariable(variableId)?.values.map(variableValue => ({
      id: variableValue.id,
      name: variableValue.value,
      title: variableValue.value
    })) || [];
  }, [getVariable]);

  return (
    <>
      <ModalWindow title={!!environment?.id ? t("environment.edit") : t("environment.add")}
                   onClose={onCancel}
                   width={814}
                   children={
                     <div className={styles.container}>
                       <div className={styles.subContainer}>
                         <div className={styles.inputs}>
                           <div className={styles.inputContainer}>
                             <span className={styles.required}>{t("common.name")}</span>
                             <div className={styles.input}>
                               <Input control={control}
                                      name={"name"}
                                      maxLength={100}
                                      error={!!errors.name ? errors.name.message : ""}/>
                             </div>
                           </div>
                           {!isSandbox && (
                             <div className={styles.inputContainer}>
                               <span>{t("common.testRun")}</span>
                               <div className={styles.input}>
                                 <Dropdown items={testRunDropdownItems}
                                           name={"testRun"}
                                           control={control}/>
                               </div>
                             </div>
                           )}
                         </div>
                       </div>

                       <div className={styles.subContainer}>
                         <div className={styles.servers}>
                           <div className={styles.title}>
                             <span>{t("environment.servers")}</span>
                             <div className={styles.addRemoveBlock} onClick={() => appendServer({name: "", url: ""})}>
                               <img src={addBlockIcon} alt=""/>
                             </div>
                           </div>
                           {serversFields.map(((server, index) => (
                             <div className={styles.server} key={server.id}>
                               <div className={styles.inputContainer}>
                                 <span className={styles.required}>{t("common.name")}</span>
                                 <div className={styles.input}>
                                   <Input control={control}
                                          name={`servers.${index}.name`}
                                          maxLength={100}
                                          error={!!errors.servers?.[index]?.name ? errors.servers?.[index]?.name?.message : ""}/>
                                 </div>
                               </div>
                               <div className={styles.inputContainer}>
                                 <span className={styles.required}>{t("environment.url")}</span>
                                 <div className={styles.input}>
                                   <Input control={control}
                                          name={`servers.${index}.url`}
                                          maxLength={255}
                                          error={!!errors.servers?.[index]?.url ? errors.servers?.[index]?.url?.message : ""}/>
                                 </div>
                               </div>
                               <div className={styles.inputContainer}>
                                 <span>{t("environment.token")}</span>
                                 <div className={styles.input}>
                                   <Input control={control}
                                          maxLength={57000}
                                          name={`servers.${index}.authToken`}
                                          error={!!errors.servers?.[index]?.authToken ? errors.servers?.[index]?.authToken?.message : ""}/>
                                 </div>
                               </div>
                               {serversFields.length !== 1 && (
                                 <div className={styles.addRemoveBlock} onClick={() => removeServerClick(index)}>
                                   <img src={removeBlock} alt=""/>
                                 </div>
                               )}
                             </div>
                           )))}
                         </div>
                       </div>

                       <div className={styles.defaultBlock}>
                         <Accordion
                           activeIndex={0}
                           maxHeight={300}
                           activeClassName={styles.active}
                           disabledAutoScroll
                           data={[{
                             title: (
                               <div className={styles.accordionTitle}>
                                 <img src={arrowIcon} alt=""
                                      className={styles.arrow}/>
                                 <span>{t("environment.defaultVariables")}</span>
                               </div>
                             ),
                             child: (
                               <div className={styles.accordionChild}>
                                 {defaultVariablesFields.map((defaultVariableField, index) => (
                                   <div className={styles.inputContainer}
                                        key={defaultVariableField.id}>
                                     <span>{getVariableName(defaultVariableField.variableId)}</span>
                                     <div className={styles.dropdown}>
                                       <Dropdown control={control}
                                                 items={getVariableDropdownItems(defaultVariableField.variableId)}
                                                 name={`defaultVariableValues.${index}.variableValueId`}/>
                                     </div>
                                   </div>
                                 ))}

                               </div>
                             )
                           }]}/>
                       </div>
                       <div className={`${styles.defaultBlock} ${styles.headers}`}>
                         <Accordion activeIndex={defaultHeaderActiveIndex}
                                    maxHeight={300}
                                    onChangeActive={setDefaultHeaderActiveIndex}
                                    activeClassName={styles.active}
                                    disabledAutoScroll
                                    data={[{
                                      title: (
                                        <div className={styles.accordionTitle}>
                                          <img src={arrowIcon} alt=""
                                               className={styles.arrow}/>
                                          <span>{t("testCaseTemplate.headers")}</span>
                                          <div className={styles.addRemoveBlock}
                                               onClick={addDefaultHeader}>
                                            <img src={addBlockIcon} alt=""/>
                                          </div>
                                        </div>
                                      ),
                                      child: (
                                        <div className={styles.accordionChild}>
                                          {defaultHeadersFields.map((field, index) => (
                                            <div className={`${styles.inputs} ${index === 0 ? styles.first : ""}`}
                                                 key={field.id}>
                                              <div className={styles.inputContainer}>
                                                <span className={styles.required}>{t("common.name")}</span>
                                                <div className={styles.textAreaContainer}>
                                                  <TextArea control={control}
                                                            maxLength={128}
                                                            name={`defaultHeaders.${index}.name`}
                                                            error={getDefaultHeadersErrorName(index)}/>
                                                </div>
                                              </div>
                                              <div className={`${styles.inputContainer} ${styles.value}`}>
                                                <ParametersInput control={control}
                                                                 name={`defaultHeaders.${index}.value`}
                                                                 title={(<span>{t("testCaseTemplate.value")}</span>)}
                                                                 showVariables={true}
                                                                 maxLength={1000}
                                                                 error={!!errors.defaultHeaders?.[index]?.value ? errors.defaultHeaders?.[index]?.value?.message : ""}/>
                                              </div>
                                              <div className={styles.addRemoveBlock}
                                                   onClick={() => removeDefaultHeaders(index)}>
                                                <img src={removeBlock} alt=""/>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )
                                    }]}/>
                       </div>
                       <div className={styles.buttonsContainer}>
                         <div className={styles.buttons}>
                           {!!environment && !!showDelete && (
                             <button className={styles.delete}
                                     disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                     onClick={onDeleteClick}>{t("common.delete")}</button>
                           )}
                         </div>
                         <div className={styles.buttons}>
                           <button className={styles.cancel}
                                   disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                   onClick={onCancel}>{t("common.cancel")}</button>
                           <button className={styles.save}
                                   disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                   onClick={onClickSave}>{t("common.save")}</button>
                         </div>
                       </div>
                     </div>
                   }
      />
      {showDeleteMessage && (
        <MessageWindow onClose={closeMessageWindow}
                       title={t("environment.delete")}
                       description={<>{t("environment.deleteDescription")} <strong>{environment?.name}</strong>?</>}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}

      {showConfirmCancel && (
        <MessageWindow onClose={closeConfirmCancelModal}
                       title={t("common.cancelChanges")}
                       description={t("common.cancelChangesDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}
    </>

  );
}